import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { CallPreset } from '@goaltree/backend/convex/lib/callPresets'
import {
  COACH_CHARACTERS,
  COACH_STYLES,
  COACH_VOICES,
  CUSTOM_VOICE,
  allowedCoachStyle,
  coachCharacter,
  coachVoiceId,
  isCoachPersona,
  isFallingBehind,
  realisticCoach,
  realisticCoachesFor,
  realisticVoice,
  resolveCoachStyle,
  resolveDynamicCoachStyle,
} from '@goaltree/backend/convex/lib/coach'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { Sheet, SheetContent } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useAction, useMutation, useQuery } from 'convex/react'
import {
  ArrowUp,
  CalendarCheck,
  CalendarClock,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  EllipsisVertical,
  Flame,
  FolderInput,
  Handshake,
  LifeBuoy,
  Lightbulb,
  ListChecks,
  Loader2,
  Mic,
  Moon,
  Pause,
  PencilLine,
  Phone,
  Plus,
  Rocket,
  RotateCcw,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Square,
  Sun,
  Target,
  Timer,
  TrendingUp,
  UserRound,
  Video,
  VideoOff,
  Volume2,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { CoachAvatar } from '@/components/coach-avatar'
import { CoachCallSummary, CoachCallView, FocusPicker } from '@/components/coach-call'
import { CoachChangeStrip } from '@/components/coach-change-log'
import { CoachCharacterList } from '@/components/coach-characters'
import { CoachChatList } from '@/components/coach-chat-list'
import {
  CoachCommitmentsPanel,
  useCommitments,
  useRemoveCommitment,
} from '@/components/coach-commitments'
import { CoachLookToggle } from '@/components/coach-look-toggle'
import { CoachNameField } from '@/components/coach-name-field'
import { CoachSheetHeader, SheetIconButton, UserBubble } from '@/components/coach-sheet-parts'
import { CoachVoiceRecorder } from '@/components/coach-voice-recorder'
import { EventRefCard } from '@/components/event-row'
import { MarkdownContent } from '@/components/markdown-content'
import { MealEstimateCard } from '@/components/meal-estimate-card'
import { SpokenText } from '@/components/spoken-text'
import { StreakFlame } from '@/components/streak-icons'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import type { CoachCallSession } from '@/hooks/use-coach-call-session'
import {
  type ChatMessage,
  type CoachTaskCard,
  localDateKey,
  useCoachChat,
} from '@/hooks/use-coach-chat'
import { useCoachDisplayName } from '@/hooks/use-coach-name'
import { useDictation } from '@/hooks/use-dictation'
import { useGoals } from '@/hooks/use-goals'
import { useIsAdmin } from '@/hooks/use-is-admin'
import { useIsPro } from '@/hooks/use-is-pro'
import { useNow } from '@/hooks/use-now'
import { useUpdatePreferences } from '@/hooks/use-update-preferences'
import { callGate, callsOutMessage } from '@/lib/call-gate'
import { useCoachCall } from '@/lib/coach-call-store'
import { revealCallFrom } from '@/lib/coach-launch'
import {
  optimisticMarkActionUndone,
  optimisticSwitchThread,
  patchTaskCards,
} from '@/lib/coach-optimistic'
import { isEnterToSend } from '@/lib/enter-to-send'
import { downscaleImage } from '@/lib/image'
import { completeNode, removeNode, uncompleteNode } from '@/lib/nodes-optimistic'
import { PLAYBOOKS, buildPlaybookContext } from '@/lib/playbooks'
import { optimisticClearCustomVoice } from '@/lib/prefs-optimistic'
import { playDoneSound } from '@/lib/sounds'
import { handleUsageLimit } from '@/lib/usage-limit'
import { goalUrl } from '@/lib/utils'
import { type CaptureConversation, openConversationCapture } from '@/store/capture.store'

const ACTION_ICONS: Record<string, React.ReactNode> = {
  create_task: <Plus className="h-3 w-3" />,
  create_goal: <Target className="h-3 w-3" />,
  mark_done: <CheckCircle2 className="h-3 w-3" />,
  mark_not_done: <RotateCcw className="h-3 w-3" />,
  create_habit: <Flame className="h-3 w-3" />,
  add_to_today: <Sun className="h-3 w-3" />,
  set_deadline: <CalendarClock className="h-3 w-3" />,
  move_tasks: <FolderInput className="h-3 w-3" />,
  habit_time: <Clock className="h-3 w-3" />,
}

const COLLAPSE_WORDS = 120

function chatDate(ts: number, lang: string, nowLabel: string) {
  const now = Date.now()
  if (now - ts < 3_600_000) return nowLabel
  const d = new Date(ts)
  if (d.toDateString() === new Date(now).toDateString())
    return d.toLocaleTimeString(lang, { hour: 'numeric', minute: '2-digit' })
  if (now - ts < 6 * 86_400_000) return d.toLocaleDateString(lang, { weekday: 'short' })
  return d.toLocaleDateString(lang, { month: 'short', day: 'numeric' })
}

function CoachRow({ voice, children }: { voice?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <CoachAvatar voice={voice} size={22} className="mt-0.5 shrink-0" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">{children}</div>
    </div>
  )
}

type CoachAction = {
  type: string
  description: string
  taskId?: Id<'nodes'>
  commitmentId?: Id<'coachCommitments'>
  undo?: 'uncomplete' | 'complete' | 'remove' | 'habitTime' | 'commitment'
  undone?: boolean
}

// A note for something the coach changed. Completions can be reversed from here
// instead of asking the coach.
function ActionNote({
  action,
  messageId,
  index,
}: {
  action: CoachAction
  messageId?: Id<'coachMessages'>
  index: number
}) {
  const { t } = useTranslation()
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((store, { taskId }) => {
    completeNode(store, taskId)
    patchTaskCards(store, taskId, { done: true })
  })
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate((store, { taskId }) => {
    uncompleteNode(store, taskId)
    patchTaskCards(store, taskId, { done: false })
  })
  const remove = useMutation(api.nodes.remove).withOptimisticUpdate((store, { nodeId }) =>
    removeNode(store, nodeId),
  )
  const restoreHabitTime = useMutation(api.habitTime.clearToday)
  const removeCommitment = useRemoveCommitment()
  const markUndone = useMutation(api.coachHistory.markActionUndone).withOptimisticUpdate(
    optimisticMarkActionUndone,
  )
  const [busy, setBusy] = useState(false)
  const canUndo =
    !!action.undo && !!(action.taskId || action.commitmentId) && !!messageId && !action.undone
  async function undo() {
    if (!canUndo || busy) return
    setBusy(true)
    try {
      if (action.undo === 'commitment') await removeCommitment({ id: action.commitmentId! })
      else if (action.undo === 'remove') await remove({ nodeId: action.taskId! })
      else if (action.undo === 'habitTime') await restoreHabitTime({ habitId: action.taskId! })
      else await (action.undo === 'uncomplete' ? uncomplete : complete)({ taskId: action.taskId! })
      await markUndone({ messageId: messageId!, index })
    } catch {
      toast.error(t('coach.undoFailed'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <span
      className={cn(
        'flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary-text',
        action.undone && 'opacity-50 line-through',
      )}
    >
      {action.type === 'mark_done' ? (
        <CheckCircle2 className="coach-check-pop h-3 w-3" />
      ) : action.type === 'commitment' ? (
        <Handshake className="h-3 w-3" />
      ) : (
        (ACTION_ICONS[action.type] ?? <CheckCircle2 className="h-3 w-3" />)
      )}
      {action.type === 'commitment'
        ? t('commitments.saved', { text: action.description })
        : action.description}
      {canUndo && (
        <button
          type="button"
          onClick={() => void undo()}
          disabled={busy}
          className="ml-1 cursor-pointer font-bold underline-offset-2 hover:underline disabled:opacity-50"
        >
          {t('common.undo')}
        </button>
      )}
    </span>
  )
}

function TaskCard({ card }: { card: CoachTaskCard }) {
  const { t } = useTranslation()
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((store, { taskId }) => {
    completeNode(store, taskId)
    patchTaskCards(store, taskId, { done: true })
  })
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate((store, { taskId }) => {
    uncompleteNode(store, taskId)
    patchTaskCards(store, taskId, { done: false })
  })
  const addToToday = useMutation(api.dailySurface.addToToday).withOptimisticUpdate(
    (store, { taskId }) => patchTaskCards(store, taskId, { inToday: true }),
  )
  const [doneOverride, setDoneOverride] = useState<boolean | null>(null)
  const [added, setAdded] = useState(false)
  useEffect(() => setDoneOverride(null), [card.done])
  const done = doneOverride ?? card.done
  const inToday = added || card.inToday

  function toggleDone() {
    if (done) {
      setDoneOverride(false)
      void uncomplete({ taskId: card._id }).catch(() => setDoneOverride(null))
      return
    }
    setDoneOverride(true)
    playDoneSound()
    void complete({ taskId: card._id }).catch(() => setDoneOverride(null))
    toast(t('today.markedDone'), {
      action: {
        label: t('common.undo'),
        onClick: () => {
          setDoneOverride(false)
          void uncomplete({ taskId: card._id }).catch(() => {})
        },
      },
    })
  }

  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-card px-2.5 py-2">
      <button
        type="button"
        onClick={toggleDone}
        aria-label={t('inbox.markDone')}
        className={cn(
          'flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border-[1.5px] transition-colors',
          done
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-muted-foreground/50 hover:border-primary',
        )}
      >
        {done && <Check className="h-2.5 w-2.5" strokeWidth={3.5} />}
      </button>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'truncate text-xs font-medium',
            done ? 'text-muted-foreground line-through' : 'text-foreground',
          )}
        >
          {card.title}
        </p>
        {(card.goalTitle || card.targetTitle) && (
          <p className="truncate text-[10px] text-muted-foreground">
            {[card.goalTitle, card.targetTitle].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      {!done &&
        (inToday ? (
          <span className="shrink-0 rounded-full border border-border/60 px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
            {t('coach.inToday')}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => {
              setAdded(true)
              void addToToday({ taskId: card._id, localDate: localDateKey() })
                .then(() => toast.success(t('coach.addedToday')))
                .catch(() => setAdded(false))
            }}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-full border border-primary/45 px-2 py-0.5 text-[10.5px] font-bold text-primary-text transition-colors hover:bg-primary/10"
          >
            {t('coach.addToday')}
          </button>
        ))}
    </div>
  )
}

function ListenButton({
  active,
  playing,
  onClick,
}: {
  active: boolean
  playing: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  const label = active ? t('coach.stopSample') : t('coach.listen')
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        '-ml-1 flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-muted',
        active ? 'text-primary-text' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {active && !playing ? (
        <Loader2 className="size-4 animate-spin" />
      ) : active ? (
        <Square className="size-3 fill-current" strokeWidth={0} />
      ) : (
        <Volume2 className="size-4" />
      )}
    </button>
  )
}

function CoachReplyBody({
  msg,
  text,
  karaoke,
  streaming,
  onShowAll,
  listen,
}: {
  msg: ChatMessage
  text: string
  karaoke: React.ReactNode | null
  streaming: boolean
  onShowAll: (goalId: Id<'nodes'>) => void
  // The Listen button: sits right under the text it reads, before any callout/cards.
  listen?: React.ReactNode
}) {
  const { t } = useTranslation()
  const now = useNow()
  const [expanded, setExpanded] = useState(false)
  const long = !streaming && text.split(/\s+/).length > COLLAPSE_WORDS
  const collapsed = long && !expanded && !karaoke
  const cards = msg.taskCards ?? []
  const hiddenCount = msg.taskGoal
    ? msg.taskGoal.openCount - cards.filter((c) => !c.done).length
    : 0
  return (
    <>
      {karaoke ?? (
        <div
          className={cn(
            collapsed &&
              'max-h-44 overflow-hidden [mask-image:linear-gradient(to_bottom,black_65%,transparent)]',
          )}
        >
          <MarkdownContent text={text} />
        </div>
      )}
      {((long && !karaoke) || listen) && (
        <div className="-mt-1 flex items-center gap-2">
          {listen}
          {long && !karaoke && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="cursor-pointer text-[11.5px] font-semibold text-primary-text"
            >
              {expanded ? t('coach.showLess') : t('coach.showMore')}
            </button>
          )}
        </div>
      )}
      {msg.callout && !streaming && (
        <div className="rounded-xl border border-primary/45 bg-primary/10 px-3 py-2 text-[12.5px] leading-snug text-foreground">
          <span className="mb-0.5 block text-[10px] font-bold uppercase tracking-[0.12em] text-primary-text">
            {t('coach.focusToday')}
          </span>
          {msg.callout}
        </div>
      )}
      {msg.eventCards && msg.eventCards.length > 0 && !streaming && (
        <div className="flex flex-col gap-1.5">
          {msg.eventCards.map((e) => (
            <EventRefCard key={e._id} event={e} now={now} />
          ))}
        </div>
      )}
      {cards.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {cards.map((c) => (
            <TaskCard key={c._id} card={c} />
          ))}
        </div>
      )}
      {msg.taskGoal && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => onShowAll(msg.taskGoal!.goalId)}
          className="cursor-pointer self-start text-[11.5px] font-semibold text-primary-text"
        >
          {t('coach.showAllTasks', { count: msg.taskGoal.openCount })} ›
        </button>
      )}
    </>
  )
}

// Completion feedback (chime + haptic) when the coach marks a task done —
// mirrors the app's manual task-completion feedback. Confetti is reserved for
// goal completion; the check-mark animation is on the reply's action chip.
function celebrateDone() {
  playDoneSound()
}

export type PlaybookContext = {
  title: string
  hook: string
  goalPrompt: string
}

const CALL_PRESET_ITEMS = [
  { preset: 'motivate', icon: Flame },
  { preset: 'start', icon: Rocket },
  { preset: 'stuck', icon: Lightbulb },
  { preset: 'windDown', icon: Moon },
  { preset: 'planDay', icon: CalendarCheck },
] as const satisfies readonly { preset: CallPreset; icon: unknown }[]

export function CoachChatSheet({
  open,
  onClose,
  initialMessage,
  playbookContext,
  startCall,
  callVideo,
  callNonce,
  session,
}: {
  open: boolean
  onClose: () => void
  initialMessage?: string
  playbookContext?: PlaybookContext
  startCall?: boolean
  callVideo?: boolean
  callNonce?: number
  session: CoachCallSession
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { isPro, isLoading: proLoading } = useIsPro()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [lookUpgradeOpen, setLookUpgradeOpen] = useState(false)
  const prefs = useQuery(api.preferences.getMyPreferences)
  const isAdmin = useIsAdmin()
  const cast = realisticCoachesFor(isAdmin)
  const updatePrefs = useUpdatePreferences()
  const [personaOpen, setPersonaOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [commitmentsOpen, setCommitmentsOpen] = useState(false)
  const toneRef = useRef<HTMLDivElement>(null)
  const openPersona = (section?: React.RefObject<HTMLElement | null>) => {
    setMenuOpen(false)
    setRenaming(false)
    setPersonaOpen(true)
    if (section) requestAnimationFrame(() => section.current?.scrollIntoView({ block: 'start' }))
  }
  const commitments = useCommitments(open)
  const openCommitments = commitments?.open.length ?? 0
  const [recorderOpen, setRecorderOpen] = useState(false)
  const clearCustomVoice = useMutation(api.preferences.clearCustomVoice).withOptimisticUpdate(
    optimisticClearCustomVoice,
  )
  const customVoiceId = (prefs as { customVoiceId?: string } | undefined)?.customVoiceId
  // Voice cloning is Pro-only — send free users to the paywall instead.
  const openRecorder = () => (isPro ? setRecorderOpen(true) : setUpgradeOpen(true))
  // NOTE: xAI gates cloning by region/team, but we force-show the record CTA for
  // testing regardless. If the region is blocked, recording fails at submit time.
  const coachVoice = (prefs as { coachVoice?: string } | undefined)?.coachVoice
  // Realistic look: pick from vaboo's cast instead of the drawn coaches.
  const realisticCast = isPro && !!realisticCoach(coachVoice)
  const canVideo = !!realisticCoach(coachVoice)
  const videoOn = (prefs as { callVideo?: boolean } | undefined)?.callVideo !== false
  const coachStyle = allowedCoachStyle(
    (prefs as { coachStyle?: string } | undefined)?.coachStyle,
    isPro || proLoading,
  )
  const lifestyle = (prefs as { lifestyle?: string } | undefined)?.lifestyle
  const activeStyle = resolveCoachStyle(coachStyle, lifestyle)
  const pinnedStyle = isCoachPersona(coachStyle) ? coachStyle : undefined
  // Auto-play coach replies aloud (default on). Manual playback stays available.
  const voiceEnabled = (prefs as { coachAutoVoice?: boolean } | undefined)?.coachAutoVoice !== false
  const coachName = useCoachDisplayName(prefs as { coachVoice?: string; coachName?: string })
  const {
    voiceCall,
    callActive,
    callActions,
    undoCallAction,
    callSummary,
    setCallSummary,
    callHistoryStart,
    transcriptSaves,
  } = session
  // A call started from the FAB runs fullscreen; the sheet only slides in
  // afterwards if the call left something in the chat.
  const callFullscreen = useCoachCall((s) => s.fullscreen) && isPro
  const setCallFullscreen = useCoachCall((s) => s.setFullscreen)
  useBackMinimizes(open && callFullscreen, () => setCallFullscreen(false))
  const minimizeCall = useCoachCall((s) => s.minimize)

  const threadsData = useQuery(api.coachHistory.listThreads, open ? { limit: 50 } : 'skip')
  // The chat just picked from the menu shows at once; the server's active thread
  // catches up in the background.
  const [pickedThreadId, setPickedThreadId] = useState<Id<'coachThreads'> | null>()
  const serverThreadId = threadsData?.activeThreadId ?? undefined
  useEffect(() => {
    setPickedThreadId(undefined)
  }, [serverThreadId])
  const activeThreadId =
    pickedThreadId === undefined ? serverThreadId : (pickedThreadId ?? undefined)
  const newThread = useMutation(api.coachHistory.newThread)
  const switchThread = useMutation(api.coachHistory.switchThread).withOptimisticUpdate(
    optimisticSwitchThread,
  )

  // Suppress TTS auto-play of messages during a live call (the call has audio).
  const {
    send,
    approveDeadline,
    isLoading,
    hasLoaded,
    error,
    history,
    requiresUpgrade,
    reset,
    isStreamingReply,
    streamingId,
    streamedText,
    speak,
    speakPreview,
    prefetchIntros,
    stopSpeaking,
    speakingId,
    voicePendingId,
    spokenId,
    spokenChars,
    previewPlaying,
  } = useCoachChat({
    autoVoice: isPro && open && !callActive && voiceEnabled,
    voice: coachVoice,
    style: activeStyle,
    threadId: activeThreadId,
  })

  const callQuota = useQuery(api.voiceCall.getVoiceCallQuota)
  const remainingCallMin = isPro && callQuota ? Math.ceil(callQuota.remainingSeconds / 60) : null
  // Relative, not a fixed minute count: the 10-min trial would otherwise always read as low.
  const callsLow =
    remainingCallMin != null &&
    !!callQuota?.limitSeconds &&
    callQuota.remainingSeconds / callQuota.limitSeconds <= 0.2
  const briefing = useQuery(api.coach.getBriefing, open ? { localDate: localDateKey() } : 'skip')
  const streakInfo = useQuery(api.nodes.getUserStreakInfo, open ? {} : 'skip')

  const [focusPickerOpen, setFocusPickerOpen] = useState(false)
  function handleStartCall() {
    void beginCall()
  }

  function callsBlocked() {
    const gate = callGate(isPro, callQuota)
    if (gate === 'upgrade') setUpgradeOpen(true)
    else if (gate === 'out') toast.info(callsOutMessage(t, i18n.language, callQuota))
    return gate !== null
  }

  function openFocusPicker() {
    if (callsBlocked()) return
    setPersonaOpen(false)
    setMenuOpen(false)
    setFocusPickerOpen(true)
  }

  async function beginCall(opts: Parameters<typeof voiceCall.start>[0] = {}) {
    if (callsBlocked()) return
    if (callActive) return
    stopSpeaking()
    setPersonaOpen(false)
    setMenuOpen(false)
    setCallFullscreen(true)
    try {
      await session.begin(opts, history.length)
    } catch (err) {
      setCallFullscreen(false)
      if (!handleUsageLimit(err)) toast.error(t('coach.callError'))
    }
  }

  const [input, setInput] = useState('')
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null)
  const [playbookGoalSelected, setPlaybookGoalSelected] = useState(false)
  const [selectedGoalTitle, setSelectedGoalTitle] = useState<string | null>(null)
  const [showPicker, setShowPicker] = useState(false)
  const [pickerPlaybook, setPickerPlaybook] = useState<PlaybookContext | null>(null)
  // Once an empty chat shows its briefing, keep it above the conversation that follows.
  const [briefingShown, setBriefingShown] = useState(false)
  const knownIdsRef = useRef<Set<string>>(new Set())
  const celebratedRef = useRef<Set<string>>(new Set())
  const celebrateInitRef = useRef(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // Prefilled prompts (e.g. "Plan tomorrow") set the text without an onChange, so size it here too.
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }, [input])

  // Dictation: fill the input via the browser's speech recognition (shared hook).
  function resizeTextarea() {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
  }

  const {
    listening,
    supported: micSupported,
    toggle: toggleDictation,
    stop: stopDictation,
  } = useDictation({
    getValue: () => input,
    onChange: setInput,
    onUpdate: () => {
      setSelectedPreset(null)
      requestAnimationFrame(resizeTextarea)
    },
  })

  const allGoals = useGoals()
  const activeGoals = allGoals?.filter((g) => !g.isInbox && g.status !== 'done') ?? []
  const liveStyle = resolveDynamicCoachStyle({
    lifestyle,
    behind: isFallingBehind(
      activeGoals.map((g) => ({ progress: g.progress, remaining: g.remainingCount })),
      streakInfo?.streak ?? 0,
    ),
  })
  const styleName = (id: string) => t(`coach.style.${id}`)
  const toneLine = pinnedStyle
    ? styleName(pinnedStyle)
    : t('coach.toneAdaptiveNow', {
        style: styleName(liveStyle).toLocaleLowerCase(i18n.language),
      })

  // Follow generation/karaoke unless the user has scrolled up. A manual scroll
  // away from the bottom disengages auto-follow; scrolling back re-engages it.
  const autoFollowRef = useRef(true)
  const currentWordRef = useRef<HTMLSpanElement | null>(null)
  // Ignore scroll events we caused ourselves (karaoke leaves unspoken text below
  // the fold, which would otherwise look like the user scrolled up).
  const suppressScrollUntilRef = useRef(0)
  // A real user scroll (touch, wheel, scrollbar drag) pauses follow immediately,
  // even mid karaoke nudge; reaching the bottom again resumes it.
  const pauseFollow = () => {
    autoFollowRef.current = false
    suppressScrollUntilRef.current = 0
  }
  const onScroll = () => {
    if (performance.now() < suppressScrollUntilRef.current) return
    const el = scrollRef.current
    if (!el) return
    // While a reply is read aloud the Pause pill sits at the bottom; reaching it
    // must not snap back to the spoken word, so follow only resumes after playback.
    if (!spokenId && el.scrollHeight - el.scrollTop - el.clientHeight < 48)
      autoFollowRef.current = true
  }

  useEffect(() => {
    if (!spokenId) autoFollowRef.current = true
  }, [spokenId])

  const prevHistoryLenRef = useRef(0)
  useEffect(() => {
    const grew = history.length > prevHistoryLenRef.current
    prevHistoryLenRef.current = history.length
    // A new message always re-engages follow (the user asked/got a reply).
    if (grew) autoFollowRef.current = true
    const el = scrollRef.current
    if (!autoFollowRef.current || !el) return
    // While karaoke plays, keep the current word near the vertical center of the
    // container. Only nudge when it drifts past a threshold so it doesn't jitter
    // word-to-word within a line.
    if (spokenId && currentWordRef.current) {
      const word = currentWordRef.current.getBoundingClientRect()
      const box = el.getBoundingClientRect()
      const delta = word.top + word.height / 2 - (box.top + box.height / 2)
      if (Math.abs(delta) > 40) {
        suppressScrollUntilRef.current = performance.now() + 250
        el.scrollTop += delta
      }
    } else if (grew || streamingId || isLoading || isStreamingReply) {
      el.scrollTop = el.scrollHeight
    }
  }, [history, isLoading, streamingId, streamedText, isStreamingReply, spokenId, spokenChars])

  useEffect(() => {
    if (!open) return
    const raf = requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    })
    return () => cancelAnimationFrame(raf)
  }, [open])

  function resetThreadUi() {
    stopSpeaking()
    setShowPicker(false)
    setPickerPlaybook(null)
    setBriefingShown(false)
    setMenuOpen(false)
  }

  useEffect(() => {
    if (open) {
      if (initialMessage) setInput(initialMessage)
    } else {
      // Closing during a call minimises it to the PiP card; the call carries on.
      if (!session.callActive) {
        setCallFullscreen(false)
        session.clear()
      }
      reset()
      resetThreadUi()
      stopDictation()
      setInput('')
      setPlaybookGoalSelected(false)
      setSelectedGoalTitle(null)
      setPersonaOpen(false)
      knownIdsRef.current = new Set()
      celebratedRef.current = new Set()
      celebrateInitRef.current = false
    }
  }, [open])

  // callNonce changes on every request, including a tap while this sheet is already open.
  // Video follows the saved pref unless the request says otherwise. Unset means video on.
  useEffect(() => {
    if (!open || !startCall || !callNonce) return
    void beginCall(callVideo === undefined ? {} : { video: callVideo })
  }, [open, startCall, callNonce, callVideo])

  useEffect(() => {
    if (requiresUpgrade) setUpgradeOpen(true)
  }, [requiresUpgrade])

  // Celebrate when a coach reply reports a task was marked done. First pass marks
  // existing messages as handled so opening the sheet doesn't replay old ones.
  useEffect(() => {
    if (!open) return
    if (!celebrateInitRef.current) {
      celebrateInitRef.current = true
      for (const m of history) if (m._id) celebratedRef.current.add(m._id)
      return
    }
    const last = history[history.length - 1]
    if (!last?._id || last.role !== 'assistant') return
    if (celebratedRef.current.has(last._id)) return
    celebratedRef.current.add(last._id)
    if (last.actions?.some((a) => a.type === 'mark_done')) celebrateDone()
  }, [history, open])

  // Warm the intro cache when the coach opens so previews play instantly.
  // Intros are free (unlike message playback), so no Pro gate here.
  useEffect(() => {
    if (!open) return
    prefetchIntros(
      realisticCoach(coachVoice)
        ? cast.map((c) => realisticVoice(c.id))
        : COACH_VOICES.map((v) => v.id),
      activeStyle,
    )
    if (coachVoice !== CUSTOM_VOICE)
      for (const c of COACH_CHARACTERS) prefetchIntros([coachVoice ?? ''], c.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeStyle, coachVoice, i18n.language])

  // Closing the persona picker stops any intro preview. The intro plays only
  // when a voice/style is actually changed (handled in the chip onClicks).
  useEffect(() => {
    if (!personaOpen) stopSpeaking()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personaOpen])

  const PRESETS = [
    t('coach.presetFocusLabel'),
    t('coach.presetStuckLabel'),
    t('coach.presetProgressLabel'),
    t('coach.presetMotivateLabel'),
  ]

  function handlePreset(i: number) {
    if (isLoading) return
    void send(PRESETS[i])
  }

  const photoUploadUrl = useMutation(api.mealPhoto.generateUploadUrl)
  const estimateMeal = useAction(api.mealPhotoNode.estimateMeal)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [photoPending, setPhotoPending] = useState(false)

  async function handlePhoto(file: File) {
    if (photoPending || isLoading) return
    const caption = input.trim()
    setInput('')
    setPhotoPending(true)
    try {
      const blob = await downscaleImage(file)
      const res = await fetch(await photoUploadUrl(), {
        method: 'POST',
        headers: { 'Content-Type': blob.type || 'image/jpeg' },
        body: blob,
      })
      const { storageId } = (await res.json()) as { storageId: Id<'_storage'> }
      const r = await estimateMeal({
        storageId,
        ...(caption && { caption }),
        ...(activeThreadId && { threadId: activeThreadId }),
        locale: i18n.language,
        tzOffset: new Date().getTimezoneOffset(),
      })
      if (r.requiresUpgrade) setUpgradeOpen(true)
    } catch (err) {
      if (!handleUsageLimit(err)) toast.error(t('numberHabit.meal.photoError'))
    } finally {
      setPhotoPending(false)
    }
  }

  function handleSend() {
    const msg = input.trim()
    if (!msg || isLoading) return
    if (listening) stopDictation()
    setInput('')
    setSelectedPreset(null)
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    void send(msg)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (isEnterToSend(e)) {
      e.preventDefault()
      handleSend()
    }
  }

  function handleGoalChip(goal: NonNullable<typeof allGoals>[number]) {
    if (!playbookContext || isLoading) return
    knownIdsRef.current = new Set(history.map((m) => m._id).filter(Boolean) as string[])
    setPlaybookGoalSelected(true)
    setSelectedGoalTitle(goal.title)
    const msg = `${playbookContext.goalPrompt}\n\nGoal: "${goal.title}" (ID: ${goal._id as string})`
    void send(msg, goal.title)
  }

  function openPicker() {
    setPickerPlaybook(null)
    setShowPicker(true)
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    })
  }

  function handlePickerGoal(goal: NonNullable<typeof allGoals>[number]) {
    if (!pickerPlaybook || isLoading) return
    const ctx = pickerPlaybook
    setShowPicker(false)
    setPickerPlaybook(null)
    const msg = `${ctx.goalPrompt}\n\nGoal: "${goal.title}" (ID: ${goal._id as string})`
    void send(msg, goal.title)
  }

  function openGoal(goalId: Id<'nodes'>) {
    const title = allGoals?.find((g) => g._id === goalId)?.title ?? ''
    onClose()
    void navigate({
      to: '/goals/$goalId',
      params: { goalId: goalUrl(title, goalId).replace('/goals/', '') },
    })
  }

  // Hands the conversation to the brain-dump sheet; Free gets the upsell instead.
  function turnIntoTasks(source: CaptureConversation) {
    setMenuOpen(false)
    if (!isPro) {
      setUpgradeOpen(true)
      return
    }
    setCallSummary(null)
    onClose()
    void Promise.allSettled(transcriptSaves.current).then(() => openConversationCapture(source))
  }

  async function startNewChat() {
    resetThreadUi()
    await newThread({}).catch(() => {})
  }

  async function openThread(threadId: Id<'coachThreads'> | null) {
    resetThreadUi()
    if (threadId === (threadsData?.activeThreadId ?? null)) return
    setPickedThreadId(threadId)
    await switchThread({ threadId }).catch(() => setPickedThreadId(undefined))
  }

  async function playMessage(msg: ChatMessage, text: string) {
    if (!isPro) {
      setUpgradeOpen(true)
      return
    }
    try {
      await speak(msg._id!, text)
    } catch (err) {
      if (!handleUsageLimit(err)) toast.error(t('coach.voiceError'))
    }
  }

  // The whole thread, so the call sits in context; long threads keep the last
  // stretch before the call.
  const transcriptFrom = Math.max(0, callHistoryStart.current - 40)
  const callTranscript = history.slice(transcriptFrom).map((m, i) => ({
    id: m._id ?? `line-${i}`,
    role: m.role,
    content: m.content,
    notes: m.actions?.map((a) => ({
      description:
        a.type === 'commitment' ? t('commitments.saved', { text: a.description }) : a.description,
      undone: !!a.undone,
    })),
  }))
  const callTranscriptStart = callHistoryStart.current - transcriptFrom
  const isUserTurn = (m: ChatMessage) => m.role === 'user' && !!m.content.trim()
  const chatHasUserTurns = history.some(isUserTurn)
  const callUserTurns = history.slice(callHistoryStart.current).filter(isUserTurn).length

  const displayHistory = playbookContext
    ? history.filter((m) => !m._id || !knownIdsRef.current.has(m._id))
    : history

  const threadEmpty = hasLoaded && displayHistory.length === 0
  useEffect(() => {
    if (open && threadEmpty && !playbookContext) setBriefingShown(true)
  }, [open, threadEmpty, playbookContext])

  const callHero = (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-primary/25 bg-linear-to-b from-primary/12 to-primary/[0.02] px-4 pt-5 pb-4 text-center">
      <CoachAvatar voice={coachVoice} size={64} />
      <p className="mt-1 text-base font-bold text-foreground">
        {t('coach.callCardTitle', { coach: coachName })}
      </p>
      <p className="text-xs text-muted-foreground">
        {remainingCallMin != null
          ? `${t('coach.callCardHint')} ${t('coach.minutesLeft', { count: remainingCallMin })}.`
          : t('coach.callCardHint')}
      </p>
      <button
        type="button"
        onClick={handleStartCall}
        className="mt-1.5 flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.99]"
      >
        <Phone className="size-4" strokeWidth={2.6} />
        {t('coach.callCoach', { coach: coachName })}
      </button>
      <button
        type="button"
        onClick={openFocusPicker}
        className="flex h-9 w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-primary/40 text-[13px] font-bold text-primary-text transition-colors hover:bg-primary/10"
      >
        <Timer className="size-4" strokeWidth={2.4} />
        {t('callView.focus.withCoach', { name: coachName })}
      </button>
      <div className="mt-1 flex flex-wrap justify-center gap-1.5">
        {CALL_PRESET_ITEMS.map(({ preset, icon: Icon }) => (
          <button
            key={preset}
            type="button"
            onClick={() => void beginCall({ preset })}
            title={t(`coach.callPreset.${preset}Desc`)}
            className="flex h-7 cursor-pointer items-center gap-1 rounded-full bg-primary/10 px-2.5 text-[11.5px] font-semibold text-primary-text transition-colors hover:bg-primary/20"
          >
            <Icon className="size-3.5" strokeWidth={2.4} />
            {t(`coach.callPreset.${preset}`)}
          </button>
        ))}
      </div>
    </div>
  )

  const showPlaybookOpener = !!playbookContext && !playbookGoalSelected
  const showBriefing = briefingShown && !playbookContext
  const showPresets = !playbookContext || playbookGoalSelected
  const lastReply = [...displayHistory].reverse().find((m) => m.role === 'assistant')
  const suggestions =
    !isLoading && lastReply && !lastReply.streaming ? (lastReply.suggestions ?? []) : []
  const threads = threadsData?.threads ?? []

  const hour = new Date().getHours()
  const dayPart = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening'
  const briefingActions = briefing
    ? [
        {
          icon: CalendarCheck,
          label: 'planToday',
          tone: 'bg-primary/12 text-primary-text',
          hint:
            briefing.tasksToday + briefing.habitsLeft === 0
              ? t(briefing.doneToday ? 'coach.briefing.allDone' : 'coach.briefing.nothingPlanned')
              : [
                  t('coach.briefing.tasksToday', { count: briefing.tasksToday }),
                  t('coach.briefing.habitsLeft', { count: briefing.habitsLeft }),
                ].join(' · '),
        },
        {
          icon: LifeBuoy,
          label: 'stuck',
          tone: 'bg-rose-500/12 text-rose-600 dark:text-rose-400',
          hint: t('coach.briefing.stuckHint'),
        },
        {
          icon: TrendingUp,
          label: 'reviewWeek',
          tone: 'bg-sky-500/12 text-sky-600 dark:text-sky-400',
          hint: (
            <>
              {t('coach.briefing.doneThisWeek', { count: briefing.doneThisWeek })}
              {briefing.streak > 0 && (
                <>
                  {' · '}
                  <StreakFlame /> {t('coach.briefing.streak', { count: briefing.streak })}
                </>
              )}
            </>
          ),
        },
      ]
    : ([] as const)

  const chip =
    'cursor-pointer rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-50'
  const chipIdle =
    'border-border/50 bg-muted/40 text-muted-foreground hover:border-border hover:text-foreground'
  const chipOn = 'border-primary/50 bg-primary/10 text-primary-text'
  const toneOn = 'border-primary bg-primary text-primary-foreground'

  return (
    <>
      <UpgradeDialog open={lookUpgradeOpen} onOpenChange={setLookUpgradeOpen} reason="voice" />
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={(o) => {
          setUpgradeOpen(o)
          if (!o) reset()
        }}
        reason="coach"
        {...(callActive && {
          title: t('callView.upgradeTitle'),
          description: t('callView.upgradeDesc'),
        })}
      />
      <CoachVoiceRecorder
        open={recorderOpen}
        onOpenChange={setRecorderOpen}
        onCloned={() => void speakPreview(CUSTOM_VOICE, activeStyle)}
      />
      {open &&
        focusPickerOpen &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
            <button
              type="button"
              aria-label={t('common.close')}
              onClick={() => setFocusPickerOpen(false)}
              className="absolute inset-0 cursor-default"
            />
            <div className="relative w-full max-w-sm">
              <FocusPicker
                name={coachName}
                remainingSeconds={callQuota?.remainingSeconds ?? null}
                onStart={(plan) => {
                  setFocusPickerOpen(false)
                  void beginCall({ focus: plan })
                }}
                onCancel={() => setFocusPickerOpen(false)}
              />
            </div>
          </div>,
          document.body,
        )}
      {open &&
        callFullscreen &&
        createPortal(
          <div
            ref={revealCallFrom}
            className="fixed inset-0 z-[60] bg-background/35 backdrop-blur-xl backdrop-saturate-150"
          >
            <div className="relative mx-auto h-full w-full max-w-md pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]">
              <CoachCallView
                call={voiceCall}
                voice={coachVoice}
                name={coachName}
                limitSeconds={callQuota?.limitSeconds ?? null}
                canUpgrade={callQuota?.plan === 'trial'}
                actions={callActions}
                onUndo={undoCallAction}
                onUpgrade={() => setUpgradeOpen(true)}
                translucent
                transcript={callTranscript}
                transcriptStart={callTranscriptStart}
                onMinimize={minimizeCall}
              />
            </div>
          </div>,
          document.body,
        )}
      {/* The sheet stays open under a fullscreen call, so minimising the call reveals it.
          Non-modal meanwhile, or it would trap focus and swallow the call's controls. */}
      <Sheet
        open={open}
        modal={!callFullscreen}
        disablePointerDismissal={callFullscreen}
        onOpenChange={(o) => {
          if (o) return
          if (callFullscreen) setCallFullscreen(false)
          else onClose()
        }}
      >
        <SheetContent
          side="right"
          // A strip of the page stays visible on phones, so you can watch the list update as the coach acts.
          className="flex w-[calc(100%-2.5rem)] flex-col p-0 sm:w-80 sm:max-w-sm"
          showCloseButton={false}
        >
          <div className="relative flex h-full min-h-0 flex-col">
            <div
              className={cn(
                'relative flex h-full min-h-0 flex-col transition',
                (callActive || callSummary) && 'pointer-events-none opacity-40 blur-sm',
              )}
            >
              <CoachSheetHeader
                avatar={
                  <CoachAvatar
                    voice={coachVoice}
                    size={48}
                    speaking={!!speakingId || previewPlaying}
                    className="shrink-0"
                  />
                }
                name={coachName}
                subtitle={toneLine}
                identityLabel={t('coach.settings')}
                expanded={personaOpen}
                onIdentityClick={() => {
                  setMenuOpen(false)
                  setRenaming(false)
                  setPersonaOpen((v) => !v)
                }}
              >
                {/* An empty chat shows the call card instead. */}
                {!callActive && !threadEmpty && (
                  <div
                    className={cn(
                      'flex shrink-0 items-stretch overflow-hidden rounded-full text-[11px] font-bold',
                      callsLow
                        ? 'bg-amber-400/15 text-amber-600 ring-1 ring-amber-400/50 dark:text-amber-300'
                        : 'bg-primary text-primary-foreground',
                    )}
                  >
                    <button
                      type="button"
                      onClick={handleStartCall}
                      title={
                        remainingCallMin != null
                          ? `${t('coach.call')} · ${t('coach.minutesLeft', { count: remainingCallMin })}`
                          : t('coach.call')
                      }
                      className={cn(
                        'flex cursor-pointer items-center gap-1 whitespace-nowrap py-1 pr-1.5 pl-2.5 transition-colors',
                        callsLow ? 'hover:bg-amber-400/15' : 'hover:bg-primary-foreground/10',
                      )}
                    >
                      <Phone className="size-3" strokeWidth={2.6} />
                      {callsLow
                        ? t('coach.callMinLeft', { count: remainingCallMin ?? 0 })
                        : t('coach.callShort')}
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        aria-label={t('coach.callOptions')}
                        title={t('coach.callOptions')}
                        className={cn(
                          'flex cursor-pointer items-center border-l pr-1.5 pl-1 transition-colors',
                          callsLow
                            ? 'border-amber-400/40 hover:bg-amber-400/15'
                            : 'border-primary-foreground/20 hover:bg-primary-foreground/10 data-popup-open:bg-primary-foreground/10',
                        )}
                      >
                        <ChevronDown className="size-3" strokeWidth={2.6} />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72 rounded-xl p-1 shadow-2xl">
                        {[
                          ...(canVideo
                            ? [
                                {
                                  icon: Video,
                                  label: t('coachNav.videoCall'),
                                  desc: t('coach.callJustTalkDesc'),
                                  opts: { video: true },
                                },
                                {
                                  icon: Phone,
                                  label: t('coachNav.voiceCall'),
                                  desc: '',
                                  opts: { video: false },
                                },
                              ]
                            : [
                                {
                                  icon: Phone,
                                  label: t('coach.callJustTalk'),
                                  desc: t('coach.callJustTalkDesc'),
                                  opts: {},
                                },
                              ]),
                          ...CALL_PRESET_ITEMS.map(({ preset, icon }) => ({
                            icon,
                            label: t(`coach.callPreset.${preset}`),
                            desc: t(`coach.callPreset.${preset}Desc`),
                            opts: { preset },
                          })),
                        ].map(({ icon: Icon, label, desc, opts }) => (
                          <DropdownMenuItem
                            key={label}
                            onClick={() => void beginCall(opts)}
                            className="cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2"
                          >
                            <Icon className="mt-0.5 size-4 text-primary-text" />
                            <span className="flex min-w-0 flex-col gap-0.5">
                              <span className="font-semibold text-foreground">{label}</span>
                              {desc && (
                                <span className="text-[11px] leading-snug text-muted-foreground">
                                  {desc}
                                </span>
                              )}
                            </span>
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        {(
                          [
                            {
                              icon: Zap,
                              label: t('coach.callQuick'),
                              desc: t('coach.callQuickDesc'),
                              opts: { quick: true },
                            },
                            {
                              icon: Timer,
                              label: t('coach.callFocus'),
                              desc: t('coach.callFocusDesc'),
                              opts: { askFocus: true },
                            },
                          ] as const
                        ).map(({ icon: Icon, label, desc, opts }) => (
                          <DropdownMenuItem
                            key={label}
                            onClick={() => void beginCall(opts)}
                            className="cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2"
                          >
                            <Icon className="mt-0.5 size-4 text-primary-text" />
                            <span className="flex min-w-0 flex-col gap-0.5">
                              <span className="font-semibold text-foreground">{label}</span>
                              <span className="text-[11px] leading-snug text-muted-foreground">
                                {desc}
                              </span>
                            </span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                <SheetIconButton
                  onClick={() => {
                    setPersonaOpen(false)
                    setMenuOpen((v) => !v)
                  }}
                  aria-label={t('coach.menu')}
                  aria-expanded={menuOpen}
                  className={cn('h-8 w-8', menuOpen && 'bg-muted text-foreground')}
                >
                  <EllipsisVertical className="h-[18px] w-[18px]" />
                </SheetIconButton>
              </CoachSheetHeader>
              <CoachChangeStrip />

              {personaOpen && (
                <>
                  <button
                    type="button"
                    aria-label={t('common.close')}
                    onClick={() => setPersonaOpen(false)}
                    className="absolute inset-0 z-20 cursor-default"
                  />
                  <div className="absolute inset-x-3 top-[78px] z-30 flex max-h-[calc(100%-90px)] flex-col gap-3 overflow-y-auto rounded-2xl border border-border bg-popover p-3 shadow-xl">
                    <CoachLookToggle onLocked={() => setLookUpgradeOpen(true)} />
                    <p className="flex items-center justify-between text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
                      {t('coach.voice')}
                      <span className="font-medium normal-case tracking-normal">
                        {t('coach.tapToHear')}
                      </span>
                    </p>
                    <div className="-mx-2 flex gap-2.5 overflow-x-auto px-2 py-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      {(realisticCast
                        ? cast.map((c) => ({
                            id: realisticVoice(c.id),
                            label: c.name,
                          }))
                        : [
                            ...(customVoiceId
                              ? [{ id: CUSTOM_VOICE, label: t('coach.customVoice') }]
                              : []),
                            ...COACH_VOICES,
                          ]
                      ).map((v) => {
                        const active = realisticCast
                          ? coachVoice === v.id
                          : v.id === CUSTOM_VOICE
                            ? coachVoice === CUSTOM_VOICE
                            : coachVoice !== CUSTOM_VOICE && coachVoiceId(coachVoice) === v.id
                        return (
                          <div key={v.id} className="relative shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                updatePrefs({ coachVoice: v.id })
                                void speakPreview(v.id, activeStyle)
                              }}
                              className={cn(
                                'flex w-12 cursor-pointer flex-col items-center gap-1 text-[10.5px]',
                                active ? 'font-bold text-foreground' : 'text-muted-foreground',
                              )}
                            >
                              <span
                                className={cn(
                                  'flex rounded-full',
                                  active && 'ring-2 ring-primary ring-offset-2 ring-offset-popover',
                                )}
                              >
                                <CoachAvatar
                                  voice={v.id}
                                  size={40}
                                  speaking={active && previewPlaying}
                                />
                              </span>
                              <span className="max-w-full truncate">{v.label}</span>
                            </button>
                            {v.id === CUSTOM_VOICE && (
                              <button
                                type="button"
                                title={t('coach.removeVoice')}
                                aria-label={t('coach.removeVoice')}
                                onClick={() => void clearCustomVoice({})}
                                className="absolute -right-0.5 -top-0.5 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border border-border bg-popover text-muted-foreground hover:text-foreground"
                              >
                                <X className="h-2.5 w-2.5" />
                              </button>
                            )}
                          </div>
                        )
                      })}
                      <button
                        type="button"
                        onClick={openRecorder}
                        title={customVoiceId ? t('coach.rerecord') : t('coach.recordVoice')}
                        className="flex w-12 shrink-0 cursor-pointer flex-col items-center gap-1 text-[10.5px] text-muted-foreground hover:text-foreground"
                      >
                        <span className="flex h-10 w-10 items-center justify-center rounded-full border-[1.5px] border-dashed border-muted-foreground/50">
                          <Mic className="h-4 w-4" />
                        </span>
                        {t('coach.clone')}
                      </button>
                    </div>
                    <div>
                      <p className="mb-1.5 text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
                        {t('coach.nameField.label')}
                      </p>
                      <CoachNameField autoFocus={renaming} onDone={() => setRenaming(false)} />
                    </div>
                    <div ref={toneRef} className="scroll-mt-3">
                      <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/70">
                        {t('coach.personality')}
                      </p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {t('coach.personalityHint')}
                      </p>
                    </div>
                    <p className="-mb-1 text-[11px] font-semibold text-muted-foreground">
                      {t('coach.everyday')}
                    </p>
                    <div className="-mx-3 -mt-1 flex gap-1.5 overflow-x-auto px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      <button
                        type="button"
                        onClick={() => {
                          updatePrefs({ coachStyle: null })
                          void speakPreview(coachVoice, liveStyle)
                        }}
                        className={cn(
                          chip,
                          'shrink-0 whitespace-nowrap',
                          !pinnedStyle ? toneOn : chipIdle,
                        )}
                      >
                        ✦ {t('coach.adaptive')}
                      </button>
                      {COACH_STYLES.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            updatePrefs({ coachStyle: s.id })
                            void speakPreview(coachVoice, s.id)
                          }}
                          className={cn(
                            chip,
                            'shrink-0 whitespace-nowrap',
                            pinnedStyle === s.id ? toneOn : chipIdle,
                          )}
                        >
                          {styleName(s.id)}
                        </button>
                      ))}
                    </div>
                    {!coachCharacter(pinnedStyle) && (
                      <p className="-mt-1.5 text-[11px] leading-snug text-muted-foreground">
                        {t(`coach.toneHint.${pinnedStyle ?? 'adaptive'}`)}
                      </p>
                    )}
                    <p className="-mb-1 text-[11px] font-semibold text-muted-foreground">
                      {t('coach.characters')}
                    </p>
                    <div className="-mx-3 -mt-1">
                      <CoachCharacterList
                        row
                        pinned={pinnedStyle}
                        onPick={(id) => updatePrefs({ coachStyle: id })}
                        onLocked={() => setLookUpgradeOpen(true)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => updatePrefs({ coachAutoVoice: !voiceEnabled })}
                      className="flex w-full cursor-pointer items-center justify-between gap-3 text-left text-[12.5px] text-foreground"
                    >
                      <span className="min-w-0">
                        {t('coach.readAloud')}
                        <span className="block text-[11px] leading-snug text-muted-foreground">
                          {t('coach.readAloudHint')}
                        </span>
                      </span>
                      <span
                        className={cn(
                          'inline-flex h-[18px] w-8 shrink-0 items-center rounded-full px-0.5 transition-colors',
                          voiceEnabled ? 'bg-primary' : 'bg-muted-foreground/30',
                        )}
                      >
                        <span
                          className={cn(
                            'h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform',
                            voiceEnabled ? 'translate-x-3.5' : 'translate-x-0',
                          )}
                        />
                      </span>
                    </button>
                  </div>
                </>
              )}

              {commitmentsOpen && (
                <>
                  <button
                    type="button"
                    aria-label={t('common.close')}
                    onClick={() => setCommitmentsOpen(false)}
                    className="absolute inset-0 z-20 cursor-default"
                  />
                  <div className="absolute inset-x-3 top-[78px] z-30 max-h-[calc(100%-90px)] overflow-y-auto rounded-2xl border border-border bg-popover p-3 shadow-xl">
                    <CoachCommitmentsPanel />
                  </div>
                </>
              )}

              {menuOpen && (
                <>
                  <button
                    type="button"
                    aria-label={t('common.close')}
                    onClick={() => setMenuOpen(false)}
                    className="absolute inset-0 z-20 cursor-default"
                  />
                  <div className="absolute right-3 top-[70px] z-30 flex max-h-[calc(100%-82px)] w-72 max-w-[calc(100%-24px)] flex-col overflow-y-auto rounded-xl border border-border bg-popover p-1 text-xs shadow-2xl">
                    <button
                      type="button"
                      onClick={() => openPersona()}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted"
                    >
                      <UserRound className="size-3.5" /> {t('coach.switchCoach')}
                    </button>
                    <button
                      type="button"
                      onClick={() => openPersona(toneRef)}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted"
                    >
                      <SlidersHorizontal className="size-3.5" /> {t('coach.tone')}
                      <span className="ml-auto truncate text-muted-foreground">{toneLine}</span>
                    </button>
                    {canVideo && (
                      <button
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={videoOn}
                        onClick={() => updatePrefs({ callVideo: !videoOn })}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted"
                      >
                        {videoOn ? (
                          <Video className="size-3.5" />
                        ) : (
                          <VideoOff className="size-3.5" />
                        )}
                        {t(videoOn ? 'callView.videoIsOn' : 'callView.videoIsOff')}
                      </button>
                    )}
                    <div className="my-1 h-px bg-border/60" />
                    <button
                      type="button"
                      onClick={() => void startNewChat()}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted"
                    >
                      ＋ {t('coach.newChat')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false)
                        setRenaming(true)
                        setPersonaOpen(true)
                      }}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted"
                    >
                      <PencilLine className="size-3.5" /> {t('coach.nameField.rename')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false)
                        setCommitmentsOpen(true)
                      }}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted"
                    >
                      <Handshake className="size-3.5" /> {t('commitments.title')}
                      {openCommitments > 0 && (
                        <span className="ml-auto rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold tabular-nums text-primary-text">
                          {openCommitments}
                        </span>
                      )}
                    </button>
                    {chatHasUserTurns && (
                      <button
                        type="button"
                        onClick={() => turnIntoTasks({ threadId: activeThreadId ?? null })}
                        className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted"
                      >
                        <ListChecks className="size-3.5" /> {t('coach.chatToTasks')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        onClose()
                        void navigate({ to: '/settings', hash: 'coach' })
                      }}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-left text-foreground hover:bg-muted"
                    >
                      <Settings className="size-3.5" /> {t('coach.settings')}
                    </button>
                    {threads.length > 0 && (
                      <CoachChatList
                        threads={threads}
                        activeId={activeThreadId ?? null}
                        onOpen={(id) => void openThread(id)}
                        formatDate={(ts) => chatDate(ts, i18n.language, t('coach.now'))}
                      />
                    )}
                  </div>
                </>
              )}

              <div
                ref={scrollRef}
                onScroll={onScroll}
                onWheel={pauseFollow}
                onTouchMove={pauseFollow}
                onPointerDown={(e) => {
                  if (e.target === e.currentTarget) pauseFollow()
                }}
                className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4"
              >
                {showPlaybookOpener ? (
                  <div className="flex flex-col gap-4">
                    <CoachRow voice={coachVoice}>
                      <MarkdownContent
                        text={`${playbookContext.hook}\n\n${t('discover.playbook.whichGoal')}`}
                      />
                    </CoachRow>
                    {allGoals === undefined ? (
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className="h-7 w-20 animate-pulse rounded-full bg-muted/40"
                          />
                        ))}
                      </div>
                    ) : activeGoals.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {activeGoals.slice(0, 6).map((goal) => (
                          <button
                            key={goal._id as string}
                            type="button"
                            onClick={() => handleGoalChip(goal)}
                            disabled={isLoading}
                            className="cursor-pointer rounded-full border border-border/60 bg-muted/40 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary-text disabled:pointer-events-none disabled:opacity-50"
                          >
                            {goal.title}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground/50">
                        No active goals yet — create one first.
                      </p>
                    )}
                  </div>
                ) : showBriefing && briefing === null && threadEmpty && !showPicker ? (
                  <div className="flex flex-col gap-4">
                    {!callActive && callHero}
                    <div className="flex flex-col items-center gap-1 text-center">
                      <p className="text-sm font-medium text-foreground/70">
                        {t('coach.emptyTitle')}
                      </p>
                      <p className="max-w-[240px] text-xs text-muted-foreground/60">
                        {t('coach.emptySubtitle')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {threadEmpty && !playbookContext && !callActive && callHero}
                    {showBriefing && briefing && (
                      <CoachRow voice={coachVoice}>
                        <p className="text-sm leading-relaxed text-foreground">
                          {briefing.firstName
                            ? t(`coach.briefing.${dayPart}`, { name: briefing.firstName })
                            : t(`coach.briefing.${dayPart}Anon`)}
                        </p>
                        {briefing.closest && (
                          <p className="text-xs text-muted-foreground">
                            {t('coach.briefing.closest')}{' '}
                            <button
                              type="button"
                              onClick={() => openGoal(briefing.closest!.goalId)}
                              className="cursor-pointer font-semibold text-foreground hover:underline"
                            >
                              {briefing.closest.title}
                            </button>{' '}
                            · {briefing.closest.progress}%
                          </p>
                        )}
                        {briefing.commitment && (
                          <button
                            type="button"
                            disabled={isLoading}
                            onClick={() =>
                              void send(
                                t('commitments.followUpPrompt', {
                                  text: briefing.commitment!.text,
                                }),
                              )
                            }
                            className="flex cursor-pointer items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-left text-[13px] text-foreground transition-colors hover:bg-amber-500/15 disabled:pointer-events-none disabled:opacity-50"
                          >
                            <Handshake className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                            <span className="min-w-0 flex-1">
                              {t('commitments.followUp', { text: briefing.commitment.text })}
                            </span>
                            <ChevronRight className="size-4 shrink-0 text-muted-foreground/50" />
                          </button>
                        )}
                        <div className="flex flex-col divide-y divide-border/60 overflow-hidden rounded-2xl border border-border/60 bg-card">
                          {briefingActions.map((a, i) => (
                            <button
                              key={a.label}
                              type="button"
                              disabled={isLoading}
                              onClick={() => void send(t(`coach.briefing.${a.label}Prompt`))}
                              className="group flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 disabled:pointer-events-none disabled:opacity-50"
                            >
                              <span
                                className={cn(
                                  'flex size-7.5 shrink-0 items-center justify-center rounded-lg',
                                  a.tone,
                                )}
                              >
                                <a.icon className="size-4" strokeWidth={2.2} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span
                                  className={cn(
                                    'block text-[13px] font-semibold',
                                    i === 0 ? 'text-primary-text' : 'text-foreground',
                                  )}
                                >
                                  {t(`coach.briefing.${a.label}`)}
                                </span>
                                <span className="block truncate text-[11.5px] tabular-nums text-muted-foreground">
                                  {a.hint}
                                </span>
                              </span>
                              <ChevronRight className="size-4 shrink-0 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5" />
                            </button>
                          ))}
                        </div>
                      </CoachRow>
                    )}
                    {playbookContext && selectedGoalTitle && (
                      <div className="flex items-center gap-2 rounded-xl border border-border/40 bg-muted/20 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-primary-text/70">
                            {playbookContext.title}
                          </p>
                          <p className="truncate text-xs text-foreground/60">{selectedGoalTitle}</p>
                        </div>
                      </div>
                    )}
                    {displayHistory.map((msg, i) => {
                      if (playbookContext && i === 0 && msg.role === 'user') return null
                      const isLast = i === displayHistory.length - 1
                      const showApproval = isLast && msg.pendingApproval && !isLoading
                      const isTyping = !!msg._id && msg._id === streamingId
                      const displayContent = isTyping
                        ? streamedText
                        : msg._id && msg._id === voicePendingId
                          ? ''
                          : msg.content
                      if (msg.role === 'user')
                        return msg.imageUrl ? (
                          <div
                            key={i}
                            className="flex max-w-[70%] flex-col items-end gap-1 self-end"
                          >
                            <img
                              src={msg.imageUrl}
                              alt=""
                              className="max-h-64 rounded-2xl rounded-tr-sm object-cover"
                            />
                            {displayContent && (
                              <p className="rounded-2xl bg-foreground px-3 py-2 text-sm leading-snug whitespace-pre-wrap break-words text-background">
                                {displayContent}
                              </p>
                            )}
                          </div>
                        ) : (
                          <UserBubble key={i}>{displayContent}</UserBubble>
                        )
                      // A change made during a call: just its note, under the coach's column.
                      if (!displayContent.trim() && msg.actions?.length)
                        return (
                          <div key={i} className="flex flex-wrap gap-1.5 pl-[30px]">
                            {msg.actions.map((action, ai) => (
                              <ActionNote
                                key={ai}
                                action={action}
                                messageId={msg._id as Id<'coachMessages'> | undefined}
                                index={ai}
                              />
                            ))}
                          </div>
                        )
                      if (!displayContent.trim() && !msg.pendingDeadlines?.length) return null
                      const streaming = isTyping || !!msg.streaming
                      return (
                        <CoachRow key={i} voice={msg.coachVoice ?? coachVoice}>
                          <CoachReplyBody
                            msg={msg}
                            text={displayContent}
                            streaming={streaming}
                            onShowAll={openGoal}
                            listen={
                              msg._id && !streaming && displayContent.trim() ? (
                                <ListenButton
                                  active={speakingId === msg._id}
                                  playing={spokenId === msg._id}
                                  onClick={() => void playMessage(msg, displayContent)}
                                />
                              ) : null
                            }
                            karaoke={
                              msg._id && msg._id === spokenId ? (
                                <SpokenText
                                  text={msg.content}
                                  chars={spokenChars}
                                  currentRef={currentWordRef}
                                />
                              ) : null
                            }
                          />
                          {msg.mealEstimate && !streaming && (
                            <MealEstimateCard messageId={msg._id} estimate={msg.mealEstimate} />
                          )}
                          {msg.actions && msg.actions.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {msg.actions.map((action, ai) => (
                                <ActionNote
                                  key={ai}
                                  action={action}
                                  messageId={msg._id as Id<'coachMessages'> | undefined}
                                  index={ai}
                                />
                              ))}
                            </div>
                          )}
                          {msg.pendingDeadlines && msg.pendingDeadlines.length > 0 && (
                            <div className="flex w-full flex-col gap-1.5 pt-1">
                              {msg.pendingDeadlines.map((d) => (
                                <div
                                  key={d.taskId}
                                  className="flex items-center gap-2 rounded-xl border border-border/40 bg-muted/20 px-3 py-2"
                                >
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-xs font-medium text-foreground">
                                      {d.taskTitle}
                                    </p>
                                    <p className="text-[10px] text-muted-foreground/70">
                                      {d.dueDateLabel}
                                    </p>
                                  </div>
                                  {d.approved ? (
                                    <CheckCircle2 className="h-4 w-4 shrink-0 text-primary-text" />
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => void approveDeadline(msg._id!, d.taskId)}
                                      className="cursor-pointer rounded-full bg-primary px-2.5 py-1 text-[10px] font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-95"
                                    >
                                      {t('coach.approve')}
                                    </button>
                                  )}
                                </div>
                              ))}
                              {msg.pendingDeadlines.some((d) => !d.approved) && (
                                <button
                                  type="button"
                                  onClick={() => void approveDeadline(msg._id!)}
                                  className="mt-0.5 cursor-pointer self-start rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-95"
                                >
                                  Approve all
                                </button>
                              )}
                            </div>
                          )}
                          {showApproval && !msg.pendingDeadlines?.length && (
                            <div className="flex gap-2 pt-1">
                              <button
                                type="button"
                                onClick={() => void send('Approved')}
                                className="cursor-pointer rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-95"
                              >
                                {t('coach.approve')}
                              </button>
                              <button
                                type="button"
                                onClick={() => void send('Declined')}
                                className="cursor-pointer rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-xs font-semibold text-foreground/70 transition-all hover:border-border hover:text-foreground active:scale-95"
                              >
                                {t('coach.decline')}
                              </button>
                            </div>
                          )}
                        </CoachRow>
                      )
                    })}

                    {photoPending && (
                      <p className="flex items-center gap-1.5 self-end text-[11px] text-muted-foreground">
                        <Loader2 className="size-3 animate-spin" />
                        {t('numberHabit.meal.analyzing')}
                      </p>
                    )}

                    {showPicker && (
                      <div className="flex flex-col gap-3">
                        <CoachRow voice={coachVoice}>
                          <MarkdownContent
                            text={
                              pickerPlaybook
                                ? `${pickerPlaybook.hook}\n\n${t('discover.playbook.whichGoal')}`
                                : t('discover.playbook.pickPlaybook')
                            }
                          />
                        </CoachRow>
                        {pickerPlaybook ? (
                          activeGoals.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {activeGoals.slice(0, 6).map((goal) => (
                                <button
                                  key={goal._id as string}
                                  type="button"
                                  onClick={() => handlePickerGoal(goal)}
                                  disabled={isLoading}
                                  className="cursor-pointer rounded-full border border-border/60 bg-muted/40 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary-text disabled:pointer-events-none disabled:opacity-50"
                                >
                                  {goal.title}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground/50">
                              No active goals yet — create one first.
                            </p>
                          )
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {PLAYBOOKS.map((p) => (
                              <button
                                key={p.key}
                                type="button"
                                onClick={() =>
                                  setPickerPlaybook(buildPlaybookContext(t, p.key, p.goalPrompt))
                                }
                                className="cursor-pointer rounded-full border border-border/60 bg-muted/40 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/10 hover:text-primary-text"
                              >
                                {t(`discover.playbook.${p.key}.title`)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {(isLoading || voicePendingId || (showBriefing && briefing === undefined)) &&
                      !streamingId &&
                      !isStreamingReply && (
                        <div className="flex items-center gap-1.5 py-1">
                          {[0, 150, 300].map((delay) => (
                            <span
                              key={delay}
                              className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/40"
                              style={{ animationDelay: `${delay}ms` }}
                            />
                          ))}
                        </div>
                      )}

                    {error && <p className="text-xs text-destructive">{error}</p>}
                  </>
                )}
                {spokenId && (
                  <div className="pointer-events-none sticky bottom-2 z-10 flex justify-center pt-2">
                    <button
                      type="button"
                      onClick={stopSpeaking}
                      className="pointer-events-auto flex cursor-pointer items-center gap-1.5 rounded-full border border-primary/40 bg-background/95 px-3.5 py-1.5 text-xs font-semibold text-primary-text shadow-lg backdrop-blur transition-colors hover:bg-muted"
                    >
                      <Pause className="size-3 fill-current" strokeWidth={0} />
                      {t('coach.pausePlayback')}
                    </button>
                  </div>
                )}
              </div>

              <div className="shrink-0 border-t border-border/40 px-2 py-3">
                <div className="flex flex-col">
                  {showPresets && displayHistory.length <= 4 && (
                    <div className="flex gap-1.5 overflow-x-auto px-2 pt-3 pb-2 [mask-image:linear-gradient(to_right,transparent,black_8px,black_calc(100%-20px),transparent)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0 [&>*]:whitespace-nowrap">
                      {suggestions.length > 0 ? (
                        suggestions.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => void send(s)}
                            disabled={isLoading}
                            className={cn(chip, chipIdle)}
                          >
                            {s}
                          </button>
                        ))
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={openPicker}
                            disabled={isLoading}
                            className={cn(chip, 'flex items-center gap-1', chipOn)}
                          >
                            <Sparkles className="h-3 w-3" />
                            {t('coach.playbooks')}
                          </button>
                          {PRESETS.map((label, i) =>
                            showBriefing && briefing && i === 1 ? null : (
                              <button
                                key={i}
                                type="button"
                                onClick={() => handlePreset(i)}
                                disabled={isLoading}
                                className={cn(chip, selectedPreset === i ? chipOn : chipIdle)}
                              >
                                {label}
                              </button>
                            ),
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <div className="rounded-[22px] border border-border/70 bg-card p-1.5">
                    <textarea
                      ref={textareaRef}
                      value={input}
                      onChange={(e) => {
                        setInput(e.target.value)
                        setSelectedPreset(null)
                        e.target.style.height = 'auto'
                        e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                      rows={1}
                      placeholder={t('coach.askName', { name: coachName })}
                      className={cn(
                        'block min-h-9 max-h-[120px] w-full resize-none overflow-y-auto bg-transparent px-2.5 pt-2 pb-1 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none disabled:opacity-50',
                      )}
                    />
                    <div className="flex items-center gap-1">
                      {micSupported && (
                        <button
                          type="button"
                          onClick={toggleDictation}
                          disabled={isLoading}
                          aria-pressed={listening}
                          aria-label={t(listening ? 'coach.stopDictation' : 'coach.dictate')}
                          className={cn(
                            'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors disabled:opacity-40',
                            listening
                              ? 'bg-destructive text-white'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          )}
                        >
                          <Mic className="size-[18px]" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={isLoading || photoPending}
                        aria-label={t('numberHabit.meal.attach')}
                        title={t('numberHabit.meal.attach')}
                        className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                      >
                        <Camera className="size-[18px]" />
                      </button>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          e.target.value = ''
                          if (file) void handlePhoto(file)
                        }}
                      />
                      <div className="flex-1" />
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={isLoading || !input.trim()}
                        aria-label={t('common.send')}
                        title={t('common.send')}
                        className={cn(
                          'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors disabled:cursor-default',
                          input.trim() && !isLoading
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                            : 'bg-muted text-muted-foreground',
                        )}
                      >
                        {isLoading ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <ArrowUp className="size-4" strokeWidth={2.25} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {callActive
              ? // One live face <video>: while fullscreen owns it, the sheet's view waits.
                !callFullscreen && (
                  <CoachCallView
                    call={voiceCall}
                    voice={coachVoice}
                    name={coachName}
                    limitSeconds={callQuota?.limitSeconds ?? null}
                    canUpgrade={callQuota?.plan === 'trial'}
                    actions={callActions}
                    onUndo={undoCallAction}
                    onUpgrade={() => setUpgradeOpen(true)}
                    transcript={callTranscript}
                    transcriptStart={callTranscriptStart}
                    onMinimize={minimizeCall}
                  />
                )
              : callSummary && (
                  <CoachCallSummary
                    voice={coachVoice}
                    name={coachName}
                    seconds={callSummary.seconds}
                    reason={callSummary.preset && t(`coach.callPreset.${callSummary.preset}`)}
                    keyPoints={callSummary.keyPoints}
                    actions={callActions}
                    onToTasks={
                      callUserTurns >= 2
                        ? () => turnIntoTasks({ since: callSummary.since })
                        : undefined
                    }
                    onBack={() => setCallSummary(null)}
                    onDone={() => {
                      setCallSummary(null)
                      onClose()
                    }}
                  />
                )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

const BACK_KEY = 'coachCallFullscreen'

// The swipe-back gesture / back button minimises a fullscreen call instead of
// leaving the page: a same-URL history entry is pushed while it's up.
function useBackMinimizes(active: boolean, onBack: () => void) {
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack
  useEffect(() => {
    if (!active) return
    window.history.pushState({ ...window.history.state, [BACK_KEY]: true }, '')
    let popped = false
    const onPop = () => {
      popped = true
      onBackRef.current()
    }
    window.addEventListener('popstate', onPop)
    return () => {
      window.removeEventListener('popstate', onPop)
      // Closed some other way: drop our entry, unless a navigation already replaced it.
      if (!popped && window.history.state?.[BACK_KEY]) window.history.back()
    }
  }, [active])
}
